// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Light and dark (M3-37, 08 §3).
//
// Three states, not two. **`system` is a real choice and the default**, distinct from having
// picked light or dark by hand: a player whose phone turns dark at sunset expects the machine to
// follow, and a product that stored `light` the moment they first loaded it in daylight has
// silently opted them out of that for good.
//
// The part that is easy to get wrong and impossible to miss once it is wrong: **the theme has to
// be on the document before the first paint.** React mounts after the stylesheet has already
// painted a body, so a dark-mode player gets a full white frame — a flash bright enough to be
// unpleasant in a dark room, on every single navigation that reloads. The fix is a few lines of
// script in the `<head>` that read the same key this module writes, which is why
// `THEME_STORAGE_KEY` and `THEME_ATTRIBUTE` are exported constants and why a test reads
// `index.html` and checks that the inline script still names both of them.

/** What the player chose. `system` is the default and is not the same as choosing a side. */
export type ThemeChoice = 'system' | 'light' | 'dark'

/** What is actually painted, once the system has been consulted. */
export type Appearance = 'light' | 'dark'

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark']

/** Where the choice is kept. The inline no-flash script in `index.html` reads the same key. */
export const THEME_STORAGE_KEY = 'hcc.pinball.theme'

/** The attribute the stylesheet keys off, set on `<html>`. */
export const THEME_ATTRIBUTE = 'data-appearance'

/** The one media query. One string, so it cannot be typed two ways. */
export const DARK_QUERY = '(prefers-color-scheme: dark)'

import { readLocal, safeStorage, writeLocal } from '../storage/local'
import { stampPreference } from '../storage/prefStamps'

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark'
}

/**
 * What to paint, given the choice and what the system says.
 *
 * Pure, and the only place the three-into-two collapse happens. Everything else — the attribute,
 * the `color-scheme` meta, the toggle's label — reads this rather than re-deriving it, because
 * two derivations disagree the first time one of them forgets that `system` exists.
 */
export function resolveAppearance(choice: ThemeChoice, systemPrefersDark: boolean): Appearance {
  if (choice === 'light') return 'light'
  if (choice === 'dark') return 'dark'
  return systemPrefersDark ? 'dark' : 'light'
}

/** The next choice in the ring, for a single-button toggle: system → light → dark → system. */
export function nextChoice(choice: ThemeChoice): ThemeChoice {
  const at = THEME_CHOICES.indexOf(choice)
  return THEME_CHOICES[(at + 1) % THEME_CHOICES.length]!
}

/**
 * What the toggle says it will do, for its `title` and its label.
 *
 * Returns a **key and its values** rather than a sentence, because the sentence has to be French
 * on a French page and this module has no translator — it is the theme controller, and giving it
 * one would put a React concern inside the thing that talks to `matchMedia`.
 */
export interface ThemeDescription {
  readonly key: 'theme.system' | 'theme.always'
  readonly appearance?: Appearance
  readonly choice?: ThemeChoice
}

export function describeChoice(choice: ThemeChoice, appearance: Appearance): ThemeDescription {
  if (choice === 'system') return { key: 'theme.system', appearance }
  return { key: 'theme.always', choice }
}

/**
 * The narrow slice of the browser this touches, so a test can supply all of it.
 *
 * A flat shape rather than a `Window`, and **assembled by `browserThemeHost` rather than
 * defaulted to `globalThis`**. `matchMedia` and `localStorage` are on `window`; `documentElement`
 * is on `document`. Passing `globalThis` therefore finds two of the three, and the theme quietly
 * stops painting: the choice is stored, the button says it is pressed, the subscribers are told —
 * and the page never changes colour, because the one call that would have changed it silently
 * did nothing. This exact mistake had already been made once in `capability.ts` and made again
 * here, which is why both now have a named assembler and a test that the assembler finds
 * everything.
 */
export interface ThemeHost {
  matchMedia?: (query: string) => {
    matches: boolean
    addEventListener?: (type: 'change', listener: (e: { matches: boolean }) => void) => void
    removeEventListener?: (type: 'change', listener: (e: { matches: boolean }) => void) => void
  }
  localStorage?: {
    getItem: (key: string) => string | null
    setItem: (key: string, value: string) => void
    removeItem?: (key: string) => void
  }
  documentElement?: {
    setAttribute: (name: string, value: string) => void
    style?: { setProperty?: (name: string, value: string) => void }
  }
}

/** The browser, assembled from the two objects its pieces actually live on. */
export function browserThemeHost(): ThemeHost {
  const global = globalThis as {
    document?: { documentElement?: ThemeHost['documentElement'] }
    matchMedia?: (query: string) => ReturnType<NonNullable<ThemeHost['matchMedia']>>
  }
  const host: ThemeHost = {}
  const root = global.document?.documentElement
  if (root !== undefined) host.documentElement = root
  const store = safeStorage()
  if (store !== undefined) host.localStorage = store
  if (global.matchMedia !== undefined) host.matchMedia = (q) => global.matchMedia!(q)
  return host
}

/**
 * The live theme: the player's choice, the system's answer, and what is painted.
 *
 * A small object with listeners rather than React state, because the same value is needed by
 * things that are not React — the document element, and the `color-scheme` property that tells
 * the browser what to paint scrollbars and form controls as. A component subscribes; the
 * document is written to directly.
 */
export class ThemeController {
  private readonly host: ThemeHost
  private readonly listeners = new Set<(appearance: Appearance, choice: ThemeChoice) => void>()
  private detachSystem: (() => void) | undefined
  private chosen: ThemeChoice
  private systemDark: boolean

  constructor(host: ThemeHost = browserThemeHost()) {
    this.host = host
    this.chosen = readChoice(host)
    this.systemDark = host.matchMedia?.(DARK_QUERY).matches === true

    const query = host.matchMedia?.(DARK_QUERY)
    if (query?.addEventListener !== undefined) {
      const listener = (e: { matches: boolean }): void => {
        this.systemDark = e.matches
        // Only worth telling anybody when it changes what is painted. Under an explicit choice
        // the system flipping at sunset must change nothing at all, which is the whole point of
        // having made the choice.
        if (this.chosen === 'system') this.publish()
      }
      query.addEventListener('change', listener)
      this.detachSystem = () => query.removeEventListener?.('change', listener)
    }
    this.paint()
  }

  get choice(): ThemeChoice {
    return this.chosen
  }

  get appearance(): Appearance {
    return resolveAppearance(this.chosen, this.systemDark)
  }

  /** What the control should say it is about to do, as a key and its values. */
  get description(): ThemeDescription {
    return describeChoice(this.chosen, this.appearance)
  }

  set(choice: ThemeChoice): Appearance {
    this.chosen = choice
    writeLocal(this.host.localStorage as never, THEME_STORAGE_KEY, choice)
    // Through the host's storage, not `safeStorage()`. This controller is built around an
    // injected host so a test can drive it without a DOM, and a stamp written somewhere else
    // would be a stamp for a value that lives in a different store.
    stampPreference('theme', this.host.localStorage as never)
    this.publish()
    return this.appearance
  }

  /** Step the ring: system → light → dark → system. */
  cycle(): ThemeChoice {
    this.set(nextChoice(this.chosen))
    return this.chosen
  }

  /** Subscribe. Returns the unsubscribe, which React's effect cleanup wants. */
  subscribe(listener: (appearance: Appearance, choice: ThemeChoice) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  dispose(): void {
    this.detachSystem?.()
    this.detachSystem = undefined
    this.listeners.clear()
  }

  private publish(): void {
    this.paint()
    for (const listener of this.listeners) listener(this.appearance, this.chosen)
  }

  /**
   * Put the answer on the document.
   *
   * Two things, not one. The attribute is what the stylesheet keys off; `color-scheme` is what
   * the *browser* keys off, and without it the scrollbars, the form controls and the default
   * canvas behind the page stay light while everything the stylesheet owns goes dark — which
   * reads as a rendering bug rather than as a missing line.
   */
  private paint(): void {
    const root = this.host.documentElement
    if (root === undefined) return
    const appearance = this.appearance
    root.setAttribute(THEME_ATTRIBUTE, appearance)
    root.style?.setProperty?.('color-scheme', appearance)
  }
}

function readChoice(host: ThemeHost): ThemeChoice {
  const stored = readLocal(host.localStorage as never, THEME_STORAGE_KEY)
  return isThemeChoice(stored) ? stored : 'system'
}
